import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, CheckCircle2, Clock, CalendarPlus, MessageSquare, Send } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const KIND_LABEL = { delay: "Retraso en la devolución", extension: "Ampliar días de alquiler", incident: "Incidencia o daño", other: "Otro aviso" };

export default function ReturnPrep() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // {kind, message, extra_days, estimated_time}
  const [sending, setSending] = useState(false);
  const [incident, setIncident] = useState(null); // null | "no" | "yes"
  const [incidentText, setIncidentText] = useState("");
  const [incidentSending, setIncidentSending] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/r/${code}/return-prep`);
      setData(d);
      setChecked(d.checked || {});
      setNotes(d.notes || "");
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const persist = async (nextChecked, nextNotes) => {
    setSaving(true);
    try {
      const { data: d } = await api.post(`/r/${code}/return-prep`, { checked: nextChecked, notes: nextNotes });
      setData(d);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id, value) => {
    const next = { ...checked, [id]: Boolean(value) };
    setChecked(next);
    persist(next, notes);
  };

  const onNotes = (v) => {
    setNotes(v);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(checked, v), 800);
  };

  const sendNotice = async () => {
    setSending(true);
    try {
      await api.post(`/r/${code}/return-notice`, {
        kind: form.kind,
        message: form.message || "",
        extra_days: form.kind === "extension" ? Number(form.extra_days || 0) || null : null,
        estimated_time: form.kind === "delay" ? form.estimated_time || null : null,
      });
      toast.success("Aviso enviado. Te responderemos lo antes posible.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  const sendIncident = async () => {
    if (!incidentText.trim()) return toast.error("Describe brevemente lo ocurrido");
    setIncidentSending(true);
    try {
      await api.post(`/r/${code}/return-notice`, { kind: "incident", message: incidentText.trim() });
      toast.success("Incidencia comunicada. Gracias por avisarnos.");
      setIncidentText("");
      setIncident(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setIncidentSending(false);
    }
  };

  if (!data)
    return <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">Cargando...</div>;

  const incidentsSent = (data.notices || []).filter((n) => n.kind === "incident");

  const { progress } = data;
  const allDone = progress.total > 0 && progress.done === progress.total;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="bg-marca text-white">
        <div className="container-x py-6 max-w-2xl">
          <Link to={`/reserva/${code}`} className="text-sm text-white/80 hover:text-white" data-testid="rp-back">← Mi reserva</Link>
          <span className="block text-overline text-white/90 mt-3">Reserva · {data.code}</span>
          <h1 className="font-display text-3xl font-black tracking-tighter mt-1">Prepara la devolución</h1>
          <div className="flex items-center gap-2 mt-3 text-sm text-white/90">
            <Calendar className="h-4 w-4" /> {fecha(data.return_date)} · {data.return_time} · {data.return_location}
          </div>
        </div>
      </div>

      <main className="container-x py-6 max-w-2xl space-y-6 fade-in">
        {data.has_checkout && (
          <div className="bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-xl p-4 text-sm text-marca-hover">
            La devolución ya está registrada. ¡Gracias por el viaje!
          </div>
        )}

        <section className="bg-white border border-[#DCD8D0] rounded-xl p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-lg font-bold text-[#1A1A1A]">Antes de devolver la furgoneta</h2>
            <span className={`text-sm font-semibold ${allDone ? "text-[#4A7C59]" : "text-[#5C5C5C]"}`} data-testid="rp-progress">
              {progress.done}/{progress.total}
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#EAE7E0] overflow-hidden mb-5">
            <div className="h-full bg-[#4A7C59] transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
          </div>
          <div className="space-y-2">
            {data.items.map((it) => (
              <label key={it.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked[it.id] ? "bg-[#4A7C59]/8 border-[#4A7C59]/40" : "bg-[#F7F5F0] border-[#DCD8D0]"}`}>
                <Checkbox checked={Boolean(checked[it.id])} onCheckedChange={(v) => toggle(it.id, v)} disabled={data.has_checkout} className="mt-0.5" data-testid={`rp-item-${it.id}`} />
                <span className={`text-sm ${checked[it.id] ? "line-through text-[#5C5C5C]" : "text-[#1A1A1A]"}`}>{it.label}</span>
              </label>
            ))}
          </div>
          {allDone && (
            <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#4A7C59]" data-testid="rp-all-done">
              <CheckCircle2 className="h-5 w-5" /> Todo listo para la devolución
            </div>
          )}
          {!data.has_checkout && (
            <div className="mt-5 bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4" data-testid="rp-incident">
              <div className="font-semibold text-[#1A1A1A]">¿Ha habido alguna incidencia o daño durante el viaje?</div>
              <p className="text-xs text-[#5C5C5C] mt-1 mb-3">Un golpe, una rotura, algo que ha dejado de funcionar… Avisarlo antes de la devolución la hace más rápida y transparente.</p>
              {incidentsSent.length > 0 && (
                <div className="mb-3 space-y-1">
                  {incidentsSent.map((n) => (
                    <div key={n.id} className="text-sm bg-white border border-[#DCD8D0] rounded-lg px-3 py-2 flex justify-between gap-3">
                      <span>{n.message}</span>
                      <span className="text-xs text-[#4A7C59] whitespace-nowrap">Comunicada ✓</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button variant={incident === "no" ? "default" : "outline"} size="sm" onClick={() => setIncident("no")} className={incident === "no" ? "bg-[#4A7C59] hover:bg-[#3D6849] text-white" : ""} data-testid="rp-incident-no">No, todo en orden</Button>
                <Button variant={incident === "yes" ? "default" : "outline"} size="sm" onClick={() => setIncident("yes")} className={incident === "yes" ? "bg-acento hover:bg-[#A84A32] text-white" : ""} data-testid="rp-incident-yes">Sí, quiero informar</Button>
              </div>
              {incident === "no" && <p className="text-sm text-[#4A7C59] mt-3">Perfecto — si surge algo antes de la devolución, puedes avisarnos aquí.</p>}
              {incident === "yes" && (
                <div className="mt-3 space-y-2">
                  <Textarea value={incidentText} onChange={(e) => setIncidentText(e.target.value)} rows={3} placeholder="¿Qué ha pasado, dónde y cuándo? Si hay un daño, describe la zona de la furgoneta." data-testid="rp-incident-text" />
                  <Button onClick={sendIncident} disabled={incidentSending || !incidentText.trim()} className="bg-acento hover:bg-[#A84A32] text-white" data-testid="rp-incident-send">
                    <Send className="h-4 w-4 mr-2" />{incidentSending ? "Enviando..." : "Comunicar incidencia"}
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="mt-5">
            <Label>¿Algo que debamos saber? (opcional)</Label>
            <Textarea value={notes} onChange={(e) => onNotes(e.target.value)} rows={2} className="mt-1" placeholder="Ej.: se ha roto una taza, lo hemos dejado anotado" disabled={data.has_checkout} data-testid="rp-notes" />
            <div className="text-xs text-[#8A8A8A] mt-1">{saving ? "Guardando…" : "Se guarda automáticamente"}</div>
          </div>
        </section>

        {!data.has_checkout && (
          <section className="bg-white border border-[#DCD8D0] rounded-xl p-5">
            <h2 className="font-display text-lg font-bold text-[#1A1A1A] mb-1">¿Algún imprevisto?</h2>
            <p className="text-sm text-[#5C5C5C] mb-4">Avísanos y lo organizamos. Recibirás respuesta por email o WhatsApp.</p>
            {!form ? (
              <div className="grid sm:grid-cols-3 gap-3">
                <button onClick={() => setForm({ kind: "delay", message: "", estimated_time: "" })} className="text-left bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 hover:border-acento transition-colors" data-testid="rp-notice-delay">
                  <Clock className="h-5 w-5 text-acento mb-2" />
                  <div className="font-semibold text-sm text-[#1A1A1A]">Llegaré más tarde</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Indícanos la hora estimada</div>
                </button>
                <button onClick={() => setForm({ kind: "extension", message: "", extra_days: 1 })} className="text-left bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 hover:border-marca transition-colors" data-testid="rp-notice-extension">
                  <CalendarPlus className="h-5 w-5 text-marca mb-2" />
                  <div className="font-semibold text-sm text-[#1A1A1A]">Quiero ampliar días</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Te confirmamos disponibilidad y precio</div>
                </button>
                <button onClick={() => setForm({ kind: "other", message: "" })} className="text-left bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 hover:border-[#E89B33] transition-colors" data-testid="rp-notice-other">
                  <MessageSquare className="h-5 w-5 text-[#E89B33] mb-2" />
                  <div className="font-semibold text-sm text-[#1A1A1A]">Otro aviso</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Cualquier otra cosa</div>
                </button>
              </div>
            ) : (
              <div className="bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 space-y-3" data-testid="rp-notice-form">
                <div className="font-semibold text-[#1A1A1A]">{KIND_LABEL[form.kind]}</div>
                {form.kind === "delay" && (
                  <div>
                    <Label>Hora estimada de llegada</Label>
                    <Input type="time" value={form.estimated_time} onChange={(e) => setForm({ ...form, estimated_time: e.target.value })} className="mt-1 max-w-[180px]" data-testid="rp-delay-time" />
                  </div>
                )}
                {form.kind === "extension" && (
                  <div>
                    <Label>Días extra</Label>
                    <Input type="number" min="1" max="60" value={form.extra_days} onChange={(e) => setForm({ ...form, extra_days: e.target.value })} className="mt-1 max-w-[140px]" data-testid="rp-extra-days" />
                  </div>
                )}
                <div>
                  <Label>Mensaje {form.kind === "other" ? "" : "(opcional)"}</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="mt-1" data-testid="rp-notice-message" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={sendNotice} disabled={sending || (form.kind === "other" && !form.message.trim())} className="bg-marca hover:bg-marca-hover text-white" data-testid="rp-notice-send">
                    <Send className="h-4 w-4 mr-2" />{sending ? "Enviando..." : "Enviar aviso"}
                  </Button>
                  <Button variant="outline" onClick={() => setForm(null)} disabled={sending}>Cancelar</Button>
                </div>
              </div>
            )}
            {data.notices?.length > 0 && (
              <div className="mt-4 space-y-2">
                {data.notices.map((n) => (
                  <div key={n.id} className="text-sm bg-white border border-[#DCD8D0] rounded-lg px-3 py-2 flex justify-between gap-3">
                    <span><strong>{KIND_LABEL[n.kind]}</strong>{n.estimated_time ? ` · ${n.estimated_time}` : ""}{n.extra_days ? ` · +${n.extra_days} días` : ""}{n.message ? ` — ${n.message}` : ""}</span>
                    <span className="text-xs text-[#4A7C59] whitespace-nowrap">Enviado ✓</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
